import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { CANONICAL_BASE, SITE_NAME, DEFAULT_OG_IMAGE, DEFAULT_LOCALE } from '../lib/seo';

export interface SEOHeadProps {
  title: string;
  description: string;
  canonical?: `/${string}`;
  ogType?: string;
  ogImage?: string;
  noIndex?: boolean;
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
  lang?: string;
  skipSuffix?: boolean;
}

export default function SEOHead({
  title,
  description,
  canonical,
  ogType = 'website',
  ogImage,
  noIndex = false,
  jsonLd,
  lang = DEFAULT_LOCALE,
  skipSuffix = false,
}: SEOHeadProps) {
  const { pathname } = useLocation();

  // Validate and sanitize canonical prop
  let sanitizedCanonical = canonical;
  if (canonical !== undefined && !canonical.startsWith('/')) {
    if (import.meta.env.DEV) {
      console.warn(
        `[SEOHead] canonical prop should be a path starting with "/", got: "${canonical}". ` +
          'Pass a path like "/pricing", not a full URL.'
      );
    }
    // Strip protocol+host if a full URL was passed, falling back to pathname
    try {
      sanitizedCanonical = new URL(canonical).pathname as `/${string}`;
    } catch {
      sanitizedCanonical = undefined;
    }
  }

  // Build canonical URL — never include query string or hash
  const rawPath = sanitizedCanonical || pathname;
  const cleanPath = rawPath.replace(/\/+$/, '') || '/';
  const canonicalUrl = `${CANONICAL_BASE}${cleanPath}`;

  const fullTitle = skipSuffix ? title : `${title} | ${SITE_NAME}`;
  const ogImageUrl = `${CANONICAL_BASE}${ogImage || DEFAULT_OG_IMAGE}`;

  return (
    <Helmet>
      <html lang={lang} />
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonicalUrl} />

      {/* Open Graph */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={ogImageUrl} />
      <meta property="og:type" content={ogType} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content={lang} />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImageUrl} />

      {/* Robots */}
      {noIndex && <meta name="robots" content="noindex,nofollow" />}

      {/* JSON-LD Structured Data */}
      {jsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
      )}
    </Helmet>
  );
}
